//TODO : Refactor after ai
/**
 * Folge-Actions: Actions aus der Notion-Datenbank, die einen neuen Datensatz
 * in einer anderen Klasse erzeugen und deshalb am Ausgangsdatensatz stehen
 * (z. B. „Kalkulation anlegen“ an der Aufnahme). Die Beschriftung sagt, was
 * passiert; `aktion` nennt die Notion-Action dahinter.
 */

import type { Datenbestand } from "./index";
import type { Datensatz } from "./types";
import {
  angebotAusAufnahme,
  ausmassAusAuftrag,
  auftragAusAngebot,
  auftragAusBestellung,
  kalkulationAusAufnahme,
  nachkalkulationAusAuftrag,
  rapportAusEinsatz,
  rechnungAusAuftrag,
  type Aenderung,
} from "./ablaeufe";

export interface FolgeAktion {
  key: string;
  label: string;
  /** Die Action aus Notion, die hier ausgelöst wird. */
  aktion: string;
  slug: string;
  rollen: string[];
  verfuegbar: (b: Datenbestand, satz: Datensatz) => boolean;
  ausfuehren: (
    b: Datenbestand,
    satz: Datensatz,
    personaId: string | null
  ) => Aenderung;
}

const hat = (b: Datenbestand, slug: string, rel: string, id: string): boolean =>
  (b[slug] ?? []).some((d) => {
    const w = d.bezuege[rel];
    return Array.isArray(w) ? w.includes(id) : w === id;
  });

export const FOLGEAKTIONEN: FolgeAktion[] = [
  {
    key: "angebot-erstellen",
    label: "Angebot aus Kalkulationen erstellen",
    aktion: "Angebot anlegen",
    slug: "baustellenaufnahme",
    rollen: ["VB", "ID"],
    verfuegbar: (b, s) =>
      (b.kalkulation ?? []).some(
        (k) =>
          k.bezuege["kalkulation-basiert-auf-baustellenaufnahme"] === s.id &&
          k.status === "Freigegeben"
      ),
    ausfuehren: (b, s, p) => angebotAusAufnahme(b, s.id, p),
  },
  {
    key: "kalkulation-rechnen",
    label: "Kalkulation aus Aufnahme rechnen",
    aktion: "Kalkulation anlegen",
    slug: "baustellenaufnahme",
    rollen: ["VB", "DI"],
    verfuegbar: (_b, s) =>
      s.status === "Aufgenommen" || s.status === "Bewertet",
    ausfuehren: (b, s) => kalkulationAusAufnahme(b, s.id),
  },
  {
    key: "auftrag-aus-angebot",
    label: "Auftrag aus Angebot anlegen",
    aktion: "Auftrag anlegen",
    slug: "angebot",
    rollen: ["ID", "VB"],
    verfuegbar: (b, s) =>
      s.status === "Angenommen" &&
      !hat(b, "auftrag", "auftrag-aus-angebot", s.id),
    ausfuehren: (b, s) => auftragAusAngebot(b, s.id),
  },
  {
    key: "auftrag-aus-bestellung",
    label: "Auftrag direkt aus Bestellung anlegen",
    aktion: "Auftrag anlegen",
    slug: "bestellung",
    rollen: ["ID"],
    verfuegbar: (b, s) =>
      s.status === "Geklärt" &&
      !hat(b, "auftrag", "auftrag-aus-bestellung", s.id) &&
      !hat(b, "angebot", "angebot-zu-bestellung", s.id),
    ausfuehren: (b, s) => auftragAusBestellung(b, s.id),
  },
  {
    key: "rapport-anlegen",
    label: "Arbeitsrapport anlegen",
    aktion: "Arbeitsrapport anlegen",
    slug: "einsatz",
    rollen: ["GF"],
    verfuegbar: (b, s) =>
      (s.status === "Ausführung freigegeben" || s.status === "Ausgeführt") &&
      !hat(b, "arbeitsrapport", "arbeitsrapport-zu-einsatz", s.id),
    ausfuehren: (b, s) => rapportAusEinsatz(b, s.id),
  },
  {
    key: "ausmass-erstellen",
    label: "Ausmaß aus Rapporten erstellen",
    aktion: "Ausmaßprotokoll anlegen",
    slug: "auftrag",
    rollen: ["GF"],
    verfuegbar: (b, s) =>
      s.werte.auftragsart === "Dienstleistung" &&
      s.status === "Freigegeben" &&
      !hat(b, "ausmassprotokoll", "ausmassprotokoll-zu-auftrag", s.id) &&
      (b.einsatz ?? []).some(
        (e) =>
          e.bezuege["einsatz-fuer-auftrag"] === s.id &&
          e.status === "Ausgeführt"
      ),
    ausfuehren: (b, s) => ausmassAusAuftrag(b, s.id),
  },
  {
    key: "rechnung-bexio",
    label: "Rechnung in Bexio erstellen",
    aktion: "Rechnung in Bexio erstellen",
    slug: "auftrag",
    rollen: ["ID"],
    verfuegbar: (b, s) =>
      (s.status === "Abrechnungsbereit" ||
        (s.status === "Freigegeben" && s.werte.auftragsart === "Handel")) &&
      !hat(b, "rechnung", "rechnung-zu-auftrag", s.id),
    ausfuehren: (b, s) => rechnungAusAuftrag(b, s.id),
  },
  {
    key: "nachkalkulation",
    label: "Nachkalkulation anlegen",
    aktion: "Nachkalkulation anlegen",
    slug: "auftrag",
    rollen: ["ID"],
    verfuegbar: (b, s) =>
      s.status === "Abgeschlossen" &&
      !hat(b, "nachkalkulation", "nachkalkulation-zu-auftrag", s.id),
    ausfuehren: (b, s) => nachkalkulationAusAuftrag(b, s.id),
  },
];

export function folgeAktionen(
  b: Datenbestand,
  slug: string,
  satz: Datensatz,
  rollenKuerzel: string
): FolgeAktion[] {
  return FOLGEAKTIONEN.filter(
    (f) =>
      f.slug === slug &&
      f.rollen.includes(rollenKuerzel) &&
      f.verfuegbar(b, satz)
  );
}
